import { Loading } from 'module/ui/components/atoms/loading';
import { Popover } from 'module/ui/components/atoms/popover';
import { Timeline } from 'api/timeline/types';
import moment from 'moment';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';

interface TimelinePeriod {
  periodKey: string;
  timelines: Timeline[];
}

const TOC_VISIBLE_LINES = 20;

const ToCContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  align-items: start;
  position: relative;
  z-index: 1;
`;

const ToCLine = styled.div<{ isActive: boolean }>`
  width: 12px;
  height: 1px;
  background-color: ${(props) =>
    props.isActive ? 'var(--button-primary)' : 'var(--gray-30)'};
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.isActive ? 1 : 0.5)};
  &:hover {
    opacity: 1;
    background-color: var(--button-primary);
  }
`;

const PopoverContent = styled.div`
  max-height: 50vh;
  overflow-y: auto;
  min-width: 250px;
  max-width: 350px;
  padding: var(--spacing-04);
  position: relative;
`;

const PopoverMonthSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: var(--spacing-03);
`;

const PopoverMonthHeader = styled.div<{ isActive: boolean; isEmpty: boolean }>`
  font-size: var(--font-size-sm);
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  text-transform: capitalize;
  margin-bottom: var(--spacing-02);
`;

const PopoverTitleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-01);
  margin-top: 2px;
`;

const PopoverTitleItem = styled.div<{ isActive: boolean }>`
  padding: var(--spacing-01) var(--spacing-03);
  cursor: pointer;
  color: ${(props) =>
    props.isActive ? 'var(--link-color)' : 'var(--text-secondary)'};
  transition: all 0.2s ease;
  font-size: var(--font-size-xs);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    background-color: var(--gray-10);
    color: var(--link-color);
    border-radius: var(--border-radius-xsmall);
  }
`;

const EmptyIndicator = styled.span`
  color: var(--text-tertiary);
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-04);
  min-height: 40px;
`;

const ToCLineLoader = styled.div`
  width: 12px;
  height: 1px;
  background-color: var(--gray-30);
  opacity: 0.5;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background-color: var(--button-primary);
    animation: loading-line 1.5s ease-in-out infinite;
  }

  @keyframes loading-line {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }
`;

interface MeetingsEmailsTOCProps {
  data: TimelinePeriod[];
  selectedMonthKey?: string;
  onMonthSelect?: (monthKey: string) => void;
  onTitleSelect?: (timelineId: string) => void;
  isLoadingDown?: boolean;
  onLoadMore?: (silent?: boolean) => void; // silent parameter for TOC loading
  /** When set, TOC lists these `YYYY-MM` keys (signal-count); timelines come from `data` when loaded. */
  signalCountMonthKeys?: string[];
}

export const MeetingsEmailsTOC: React.FC<MeetingsEmailsTOCProps> = ({
  data,
  selectedMonthKey,
  onMonthSelect,
  onTitleSelect,
  isLoadingDown = false,
  onLoadMore,
  signalCountMonthKeys,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const monthRefs = useRef<Map<string, HTMLElement>>(new Map());

  const loadedMonths = useMemo(() => {
    if (signalCountMonthKeys?.length) {
      return signalCountMonthKeys.map((key) => ({
        key,
        label: moment(key, 'YYYY-MM').format('MMM YYYY'),
      }));
    }

    return data
      .map((period) => ({
        key: period.periodKey,
        label: moment(period.periodKey, 'YYYY-MM').format('MMM YYYY'),
      }))
      .sort((a, b) => {
        const dateA = moment(a.key, 'YYYY-MM');
        const dateB = moment(b.key, 'YYYY-MM');

        return dateB.valueOf() - dateA.valueOf();
      });
  }, [data, signalCountMonthKeys]);

  // Create a map of periodKey to timelines for quick lookup
  const dataMap = useMemo(() => {
    const map = new Map<string, Timeline[]>();

    data.forEach((period) => {
      map.set(period.periodKey, period.timelines);
    });

    return map;
  }, [data]);

  const handleMonthClick = useCallback(
    (monthKey: string) => {
      if (onMonthSelect) {
        onMonthSelect(monthKey);
      }
      setIsPopoverOpen(false);
    },
    [onMonthSelect],
  );

  const handleTitleClick = useCallback(
    (timelineId: string) => {
      if (onTitleSelect) {
        onTitleSelect(timelineId);
      }
    },
    [onTitleSelect],
  );

  // Popover content with only loaded months and their titles
  const popoverContent = useMemo(
    () => (
      <PopoverContent>
        {loadedMonths.map((month) => {
          const timelines = dataMap.get(month.key) || [];
          const isEmpty = timelines.length === 0;
          const isActive = selectedMonthKey === month.key;

          return (
            <PopoverMonthSection
              key={month.key}
              ref={(el) => {
                if (el) {
                  monthRefs.current.set(month.key, el);
                } else {
                  monthRefs.current.delete(month.key);
                }
              }}
              data-month-key={month.key}
            >
              <PopoverMonthHeader
                isActive={isActive}
                isEmpty={isEmpty}
                onClick={() => handleMonthClick(month.key)}
              >
                {month.label.toLowerCase()}
                {isEmpty && <EmptyIndicator>-</EmptyIndicator>}
              </PopoverMonthHeader>
              {!isEmpty && timelines.length > 0 && (
                <PopoverTitleList>
                  {timelines
                    .sort((a, b) => {
                      const dateA = moment(
                        a.startTime || a.createdAt || a.updatedAt,
                      );
                      const dateB = moment(
                        b.startTime || b.createdAt || b.updatedAt,
                      );

                      if (!dateA.isValid() || !dateB.isValid()) {
                        return 0;
                      }

                      return dateB.valueOf() - dateA.valueOf();
                    })
                    .map((timeline) => {
                      const title =
                        timeline.source?.title || 'Untitled Meeting/Email';

                      return (
                        <PopoverTitleItem
                          key={timeline.source.id}
                          isActive={false}
                          onClick={() =>
                            handleTitleClick(
                              `${timeline.source?.title || ''}${timeline.source?.id || ''}`,
                            )
                          }
                          title={title}
                        >
                          {title}
                        </PopoverTitleItem>
                      );
                    })}
                </PopoverTitleList>
              )}
            </PopoverMonthSection>
          );
        })}
        {/* Show loader at bottom when loading more */}
        {isLoadingDown && (
          <LoaderContainer>
            <Loading size="small" />
          </LoaderContainer>
        )}
        {/* Sentinel element for intersection observer - placed at bottom */}
        <div
          data-toc-sentinel="true"
          style={{
            height: '1px',
            width: '100%',
            visibility: 'hidden',
            pointerEvents: 'none',
            flexShrink: 0,
            minHeight: '1px',
          }}
        />
      </PopoverContent>
    ),
    [
      loadedMonths,
      dataMap,
      selectedMonthKey,
      handleMonthClick,
      handleTitleClick,
      isLoadingDown,
    ],
  );

  // No longer need intersection observer for individual months
  // since we only show loaded months now

  // Set up intersection observer for loading more when scrolling to end of popover
  useEffect(() => {
    if (!onLoadMore || !isPopoverOpen) {
      // Clean up when popover closes
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      return;
    }

    let sentinel: HTMLElement | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    // Wait for popover to be fully rendered before setting up observer
    timeoutId = setTimeout(() => {
      // Find the popover content element
      const popoverElement = document.querySelector(
        '.ant-popover-content .ant-popover-inner-content',
      ) as HTMLElement;

      if (!popoverElement) {
        return;
      }

      // Find the sentinel element (should already exist in the popover content)
      sentinel = popoverElement.querySelector(
        '[data-toc-sentinel]',
      ) as HTMLElement;

      if (!sentinel) {
        // Fallback: create sentinel if it doesn't exist (shouldn't happen)
        sentinel = document.createElement('div');
        sentinel.setAttribute('data-toc-sentinel', 'true');
        sentinel.style.height = '1px';
        sentinel.style.width = '100%';
        sentinel.style.visibility = 'hidden';
        sentinel.style.pointerEvents = 'none';
        sentinel.style.flexShrink = '0';
        popoverElement.appendChild(sentinel);
      }

      // Disconnect existing observer if any
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Create intersection observer to detect when we reach the end
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Trigger when sentinel is visible (we've reached the end)
            // Only trigger if not already loading
            if (entry.isIntersecting && !isLoadingDown && onLoadMore) {
              // Call loadMore with silent=true to prevent main content loader
              // This will load earlier periods until billing start date silently
              onLoadMore(true);
            }
          });
        },
        {
          root: popoverElement,
          rootMargin: '200px', // Trigger 200px before reaching the bottom for better UX
          threshold: 0, // Trigger as soon as any part is visible
        },
      );

      // Observe the sentinel
      if (sentinel) {
        observerRef.current.observe(sentinel);
      }
    }, 150); // Small delay to ensure popover is fully rendered

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [onLoadMore, isLoadingDown, isPopoverOpen, data.length]);

  // Re-observe sentinel when data changes (new periods loaded)
  // This ensures the observer continues to work after new data is loaded
  useEffect(() => {
    if (!isPopoverOpen || !observerRef.current) {
      return;
    }

    // Re-observe sentinel after data updates to ensure it's still being watched
    const timeoutId = setTimeout(() => {
      const popoverElement = document.querySelector(
        '.ant-popover-content .ant-popover-inner-content',
      ) as HTMLElement;

      if (!popoverElement) {
        return;
      }

      const sentinel = popoverElement.querySelector(
        '[data-toc-sentinel]',
      ) as HTMLElement;

      if (sentinel && observerRef.current) {
        // Disconnect and re-observe to ensure it's working
        observerRef.current.disconnect();
        observerRef.current.observe(sentinel);
      }
    }, 200);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isPopoverOpen, data.length, isLoadingDown]);

  // No longer need to re-observe since we only show loaded months

  // Only show first 20 loaded months as lines
  const visibleLineItems = useMemo(
    () => loadedMonths.slice(0, TOC_VISIBLE_LINES),
    [loadedMonths],
  );

  if (loadedMonths.length === 0 && !isLoadingDown) {
    return null;
  }

  return (
    <Popover
      content={popoverContent}
      trigger="hover"
      placement="right"
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
      overlayStyle={{ zIndex: 1000 }}
      mouseEnterDelay={0}
      mouseLeaveDelay={0.1}
      align={{ offset: [-70, 50] }}
    >
      <div className="table-of-contents">
        <ToCContainer>
          {visibleLineItems.map((item) => {
            const isActive = selectedMonthKey === item.key;

            return (
              <ToCLine
                key={item.key}
                isActive={isActive}
                onClick={() => handleMonthClick(item.key)}
                title={item.label}
              />
            );
          })}
          {/* Show loader line when loading more */}
          {isLoadingDown && <ToCLineLoader />}
        </ToCContainer>
      </div>
    </Popover>
  );
};
